import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, TrendingUp, Wrench, FileText, Award, Sparkles,
  Users, DollarSign, Activity, ArrowUpRight, Building2, ExternalLink, Plus, Download,
} from "lucide-react";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";

/**
 * DIGITAL PASSPORT — asset-agnostic permanent identity dashboard.
 * Route: /passport/:assetType/:assetId
 */
export default function DigitalPassport() {
  const { assetType = "vehicle", assetId } = useParams();
  const [asset, setAsset] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [chain, setChain] = useState({ chain: [] });
  const [resale, setResale] = useState(null);
  const [pred, setPred] = useState(null);
  const [trustedUpdates, setTrustedUpdates] = useState([]);

  const load = async () => {
    try {
      if (assetType === "vehicle") {
        const v = await api.get(`/vehicles/${assetId}`);
        setAsset(v.data);
      }
      const [tl, ch, rr, pm, tu] = await Promise.all([
        api.get(`/passport/${assetId}/timeline`).catch(() => ({ data: { events: [] } })),
        api.get(`/passport/${assetId}/ownership-chain`).catch(() => ({ data: { chain: [] } })),
        api.get(`/passport/${assetId}/resale-readiness`).catch(() => ({ data: null })),
        api.get(`/passport/${assetId}/predictive-maintenance`).catch(() => ({ data: null })),
        api.get(`/network/assets/${assetId}/updates`).catch(() => ({ data: [] })),
      ]);
      let events = tl.data?.events || tl.data || [];
      if (!Array.isArray(events)) events = [];
      setTimeline(events); setChain(ch.data); setResale(rr.data); setPred(pm.data); setTrustedUpdates(tu.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assetType, assetId]);

  const title = asset ? `${asset.year || ""} ${asset.make || ""} ${asset.model || ""}`.trim() : "Digital Passport";
  const identity = asset?.vin || asset?.registration_number || assetId;

  return (
    <div>
      <Navbar />
      <section className="bg-gradient-to-br from-primary via-primary/95 to-emerald-900 text-primary-foreground border-b border-primary/40">
        <div className="max-w-[1400px] mx-auto px-6 py-10 grid md:grid-cols-[1fr_auto] gap-8 items-start">
          <div>
            <div className="flex items-center gap-2 text-primary-foreground/80 text-[10px] mono uppercase tracking-widest">
              THOM Digital Passport™ · {assetType.charAt(0).toUpperCase() + assetType.slice(1)}Verify™
            </div>
            <h1 className="display text-4xl sm:text-5xl mt-1 leading-tight">{title}</h1>
            <div className="mono text-xs mt-2 text-primary-foreground/80 break-all">Passport ID: {assetId}</div>
            <div className="mono text-xs text-primary-foreground/80">Identity: {identity}</div>
          </div>
          <div className="border border-primary-foreground/25 rounded-sm p-4 min-w-[240px] bg-primary-foreground/5 backdrop-blur flex items-start gap-4">
            {asset?.verification_status === "verified" && asset?.certificate_number && (
              <div className="bg-white/95 rounded-full p-1 shrink-0">
                <ThomVerifiedStamp size={96} verificationStatus="verified" certificateId={asset.certificate_number} trustScore={asset.trust_score} clickable showTooltip />
              </div>
            )}
            <div className="min-w-0">
              <div className="kicker text-primary-foreground/70">Live Status</div>
              <div className="mt-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4" />
                <div className="display text-lg">{asset?.verification_status ? asset.verification_status.replace(/_/g," ").toUpperCase() : "—"}</div>
              </div>
              {asset?.trust_score && (
                <div className="mt-3 pt-3 border-t border-primary-foreground/20">
                  <div className="text-xs text-primary-foreground/70">Trust Score</div>
                  <div className="display text-2xl">{asset.trust_score}/100</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 -mt-6 mb-8 relative z-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Resale Readiness" value={resale ? `${resale.resale_readiness_score}` : "—"} sub={resale?.band || ""} Icon={TrendingUp} />
          <Kpi label="Events on Passport" value={timeline.length} sub="lifetime records" Icon={Activity} />
          <Kpi label="Ownership Periods" value={chain?.chain?.length || 1} sub={`${chain?.chain?.length > 1 ? "transfers recorded" : "original owner"}`} Icon={Users} />
          <Kpi label="Trusted Contributors" value={new Set(trustedUpdates.map(u => u.org_id)).size} sub={`${trustedUpdates.length} updates`} Icon={Building2} />
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-16">
        <Tabs defaultValue="timeline">
          <TabsList>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="service" data-testid="tab-service">Record Service</TabsTrigger>
            <TabsTrigger value="resale" data-testid="tab-resale">Resale Readiness</TabsTrigger>
            <TabsTrigger value="predictive" data-testid="tab-predictive">Predictive Maintenance</TabsTrigger>
            <TabsTrigger value="ownership" data-testid="tab-ownership">Ownership</TabsTrigger>
            <TabsTrigger value="trusted" data-testid="tab-trusted">Trusted Network</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="timeline" className="mt-6"><TimelineView events={timeline} /></TabsContent>
          <TabsContent value="service" className="mt-6"><ServiceRecordForm assetId={assetId} assetType={assetType} onSaved={load} /></TabsContent>
          <TabsContent value="resale" className="mt-6">{resale ? <ResaleCard rr={resale} /> : <Empty message="No resale-readiness data available." />}</TabsContent>
          <TabsContent value="predictive" className="mt-6">{pred ? <PredictiveCard pm={pred} /> : <Empty message="Predictive maintenance requires additional service history." />}</TabsContent>
          <TabsContent value="ownership" className="mt-6"><OwnershipChain chain={chain} /></TabsContent>
          <TabsContent value="trusted" className="mt-6"><TrustedUpdates updates={trustedUpdates} /></TabsContent>
          <TabsContent value="documents" className="mt-6"><DocumentsView asset={asset} /></TabsContent>
        </Tabs>
      </section>
      <Footer />
    </div>
  );
}

const Kpi = ({ label, value, sub, Icon }) => (
  <div className="border border-border bg-card p-5 rounded-sm shadow-sm">
    <div className="flex items-start justify-between">
      <div><div className="kicker">{label}</div><div className="display text-3xl mt-1">{value}</div>{sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}</div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
  </div>
);
const Empty = ({ message }) => <div className="border border-border p-10 bg-card mono text-sm text-muted-foreground text-center">{message}</div>;

const EVENT_ICON = {
  service_recorded: Wrench, insurance_recorded: FileText, accident_reported: FileText,
  ownership_transferred: Users, verified: ShieldCheck, certificate_issued: Award,
  certificate_revoked: Award, escrow_committed: DollarSign, escrow_released: DollarSign, escrow_refunded: DollarSign,
  listed: Sparkles, imported: FileText, duty_paid: FileText, duty_confirmed: FileText,
};

/** Timeline with cross-links to escrow, certificates, badges, PDFs. */
const TimelineView = ({ events }) => {
  if (!events?.length) return <Empty message="No events recorded yet." />;
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
      <div className="space-y-4">
        {events.map((e) => {
          const Icon = EVENT_ICON[e.kind] || Activity;
          const escrowRef = e.meta?.reference && e.kind.startsWith("escrow_") ? e.meta.reference : null;
          const certNum = e.meta?.certificate_number || null;
          return (
            <div key={e.id} className="pl-12 relative" data-testid={`timeline-${e.id}`}>
              <div className="absolute left-0 top-0 h-9 w-9 rounded-full bg-primary/10 border-2 border-primary grid place-items-center"><Icon className="h-4 w-4 text-primary" /></div>
              <div className="border border-border bg-card p-4 rounded-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">{e.kind.replace(/_/g, " ")}</div>
                    <div className="display text-lg leading-tight mt-0.5">{e.title}</div>
                    {e.detail && <div className="text-sm text-muted-foreground mt-1">{e.detail}</div>}
                    {e.meta?.organisation_name && (
                      <div className="text-xs text-primary mt-2 flex items-center gap-1.5">
                        <Building2 className="h-3 w-3" /> Contributed by {e.meta.organisation_name} <span className="mono uppercase text-[10px]">({e.meta.tier})</span>
                      </div>
                    )}
                    {/* Cross-links */}
                    {(escrowRef || certNum) && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`timeline-links-${e.id}`}>
                        {escrowRef && (
                          <Link to="/admin/escrow" className="mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm bg-primary/10 text-primary inline-flex items-center gap-1" data-testid={`link-escrow-${escrowRef}`}>
                            <ExternalLink className="h-3 w-3" /> Escrow · {escrowRef}
                          </Link>
                        )}
                        {certNum && (
                          <>
                            <Link to={`/verify/${certNum}`} className="mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-700 inline-flex items-center gap-1" data-testid={`link-verify-${certNum}`}>
                              <ShieldCheck className="h-3 w-3" /> Public verification page
                            </Link>
                            <a href={`${backendUrl}/api/public/certificates/${certNum}/pdf`} target="_blank" rel="noreferrer" className="mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm border border-border inline-flex items-center gap-1" data-testid={`link-pdf-${certNum}`}>
                              <Download className="h-3 w-3" /> Certificate PDF
                            </a>
                            <a href={`${backendUrl}/api/public/certificates/${certNum}/badge?variant=square`} target="_blank" rel="noreferrer" className="mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm border border-border inline-flex items-center gap-1" data-testid={`link-badge-${certNum}`}>
                              <Download className="h-3 w-3" /> Verified badge
                            </a>
                          </>
                        )}
                        {e.kind === "service_recorded" && e.meta?.reading_value != null && (
                          <span className="mono text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm bg-muted text-muted-foreground">
                            {e.meta.reading_type ? `${e.meta.reading_type.replace(/_/g,' ')}: ` : ""}
                            {Number(e.meta.reading_value).toLocaleString()} {e.meta.reading_unit || ""}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs mono text-muted-foreground whitespace-nowrap">{new Date(e.ts).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Asset-agnostic Service record form — reading_value + reading_unit + reading_type. */
const SERVICE_KINDS = [
  "scheduled_service", "maintenance", "repair", "valuation", "calibration",
  "audit", "safety_check", "cleaning", "inspection", "certification",
  "insurance_recorded", "quality_grade",
];
const READING_PRESETS = [
  { type: "odometer",        unit: "km",     label: "Vehicle · Odometer (km)" },
  { type: "operating_hours", unit: "hours",  label: "Equipment · Operating hours" },
  { type: "floor_area",      unit: "m²",     label: "Property · Floor area (m²)" },
  { type: "weight",          unit: "tonnes", label: "Commodity · Weight (tonnes)" },
  { type: "cycles",          unit: "cycles", label: "Machinery · Cycles" },
  { type: "elapsed",         unit: "days",   label: "Consignment · Elapsed days" },
  { type: "elapsed",         unit: "years",  label: "Asset age (years)" },
  { type: "custom",          unit: "",       label: "Custom" },
];

const ServiceRecordForm = ({ assetId, assetType, onSaved }) => {
  const [kind, setKind] = useState("scheduled_service");
  const [detail, setDetail] = useState("");
  const [preset, setPreset] = useState(0);
  const [readingValue, setReadingValue] = useState("");
  const [readingUnit, setReadingUnit] = useState("km");
  const [readingType, setReadingType] = useState("odometer");
  const [costUsd, setCostUsd] = useState("");
  const [org, setOrg] = useState("");
  const [busy, setBusy] = useState(false);

  const applyPreset = (i) => {
    setPreset(i);
    const p = READING_PRESETS[i];
    setReadingUnit(p.unit);
    setReadingType(p.type);
  };

  const submit = async () => {
    if (!readingValue && !detail) { toast.error("Enter a reading or a description"); return; }
    setBusy(true);
    try {
      const params = { kind };
      if (detail) params.detail = detail;
      if (readingValue) params.reading_value = parseFloat(readingValue);
      if (readingUnit) params.reading_unit = readingUnit;
      if (readingType) params.reading_type = readingType;
      if (costUsd) params.cost_usd = parseFloat(costUsd);
      if (org) params.organisation = org;
      await api.post(`/passport/${assetId}/service`, null, { params });
      toast.success("Service record added to Passport");
      setReadingValue(""); setDetail(""); setCostUsd(""); setOrg("");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-6 grid md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <div className="kicker">Add a service / maintenance record</div>
        <p className="text-xs text-muted-foreground mt-1">
          Asset-agnostic — captures <code className="mono">reading_value + reading_unit + reading_type</code>.
          Vehicle 87,000 km · Generator 4,250 hours · Property 680 m² · Commodity 25 tonnes · Equipment 12,400 cycles.
        </p>
      </div>
      <div>
        <div className="kicker mb-1">Service kind</div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="svc-kind"><SelectValue /></SelectTrigger>
          <SelectContent>{SERVICE_KINDS.map(k => <SelectItem key={k} value={k}>{k.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="kicker mb-1">Reading preset</div>
        <Select value={String(preset)} onValueChange={(v) => applyPreset(parseInt(v))}>
          <SelectTrigger data-testid="svc-preset"><SelectValue /></SelectTrigger>
          <SelectContent>{READING_PRESETS.map((p, i) => <SelectItem key={i} value={String(i)}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <div className="kicker mb-1">Reading value</div>
        <Input type="number" placeholder="87000" value={readingValue} onChange={(e) => setReadingValue(e.target.value)} data-testid="svc-reading-value" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="kicker mb-1">Reading unit</div>
          <Input placeholder="km / hours / m² / tonnes / cycles" value={readingUnit} onChange={(e) => setReadingUnit(e.target.value)} data-testid="svc-reading-unit" />
        </div>
        <div>
          <div className="kicker mb-1">Reading type</div>
          <Input placeholder="odometer / operating_hours / floor_area" value={readingType} onChange={(e) => setReadingType(e.target.value)} data-testid="svc-reading-type" />
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="kicker mb-1">Detail (optional)</div>
        <Input placeholder="e.g. Front brake pads replaced; front-left rotor resurfaced." value={detail} onChange={(e) => setDetail(e.target.value)} data-testid="svc-detail" />
      </div>
      <div>
        <div className="kicker mb-1">Cost (USD, optional)</div>
        <Input type="number" placeholder="450" value={costUsd} onChange={(e) => setCostUsd(e.target.value)} data-testid="svc-cost" />
      </div>
      <div>
        <div className="kicker mb-1">Organisation (optional)</div>
        <Input placeholder="Signed workshop / verified partner" value={org} onChange={(e) => setOrg(e.target.value)} data-testid="svc-org" />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button onClick={submit} disabled={busy} data-testid="svc-submit"><Plus className="h-3.5 w-3.5 mr-1" /> Add to Passport</Button>
      </div>
    </div>
  );
};

const ResaleCard = ({ rr }) => (
  <div className="grid md:grid-cols-[280px_1fr] gap-6">
    <div className="border border-border p-6 bg-card text-center">
      <div className="kicker">Resale Readiness</div>
      <div className="display text-6xl mt-2">{rr.resale_readiness_score}</div>
      <div className="mono text-xs uppercase tracking-widest text-muted-foreground">out of 100</div>
      <div className={`inline-block mt-3 px-3 py-1 rounded-sm text-xs mono uppercase tracking-widest ${
        rr.band === "Ready" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" :
        rr.band === "Needs Preparation" ? "bg-amber-100 text-amber-800 border border-amber-300" :
        "bg-red-100 text-red-800 border border-red-300"
      }`}>{rr.band}</div>
    </div>
    <div className="space-y-3">
      <div className="border border-border p-6 bg-card">
        <div className="kicker">Contributing signals</div>
        <ul className="mt-3 space-y-1 text-sm">
          {rr.reasons.map((r, i) => <li key={i} className="flex items-start gap-2"><ArrowUpRight className="h-3.5 w-3.5 text-primary mt-1" />{r}</li>)}
        </ul>
      </div>
      {rr.recommendations?.length > 0 && (
        <div className="border border-border p-6 bg-card">
          <div className="kicker">Recommendations</div>
          <ul className="mt-3 space-y-1 text-sm">
            {rr.recommendations.map((r, i) => <li key={i} className="flex items-start gap-2"><Sparkles className="h-3.5 w-3.5 text-accent mt-1" />{r}</li>)}
          </ul>
        </div>
      )}
    </div>
  </div>
);

/** Asset-agnostic predictive maintenance card. */
const PredictiveCard = ({ pm }) => {
  const unit = pm.latest_reading_unit || "";
  const readingLabel = pm.latest_reading_type ? pm.latest_reading_type.replace(/_/g,' ') : "current reading";
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {pm.predictions.map((p, i) => (
        <div key={i} className="border border-border p-5 bg-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="kicker">{p.item}</div>
              <div className="display text-2xl mt-1">
                {p.due_in != null ? `${p.due_in.toLocaleString()} ${p.unit || ""}` : p.due_in_years != null ? `${p.due_in_years}y` : "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{p.reason}</div>
            </div>
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground border border-border px-2 py-0.5">{p.confidence}</div>
          </div>
        </div>
      ))}
      <div className="border border-border p-5 bg-card md:col-span-2">
        <div className="kicker">{readingLabel}</div>
        <div className="display text-3xl mt-1 capitalize">
          {pm.latest_reading != null ? Number(pm.latest_reading).toLocaleString() : "—"}{" "}
          <span className="text-base mono lowercase">{unit}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{pm.service_history_count} recorded service event(s) feeding predictions · asset type: {pm.asset_type}.</div>
      </div>
    </div>
  );
};

const OwnershipChain = ({ chain }) => {
  if (!chain?.chain?.length) return <Empty message="No ownership history recorded." />;
  return (
    <div className="border border-border bg-card divide-y divide-border">
      {chain.chain.map((p, i) => (
        <div key={i} className="p-4 flex items-center gap-4">
          <div className="h-8 w-8 grid place-items-center border border-border rounded-full mono text-xs">{i + 1}</div>
          <div className="flex-1">
            <div className="font-semibold">{p.owner?.name || p.owner_id}</div>
            <div className="text-xs text-muted-foreground">{p.owner?.email}</div>
          </div>
          <div className="text-xs mono text-muted-foreground text-right">
            {p.opened_at ? new Date(p.opened_at).toLocaleDateString() : "—"} →{" "}
            {p.closed_at ? new Date(p.closed_at).toLocaleDateString() : "present"}
          </div>
        </div>
      ))}
    </div>
  );
};

const TrustedUpdates = ({ updates }) => {
  if (!updates.length) return <Empty message="No verified organisation has posted an update yet." />;
  return (
    <div className="border border-border bg-card divide-y divide-border">
      {updates.map(u => (
        <div key={u.id} className="p-4 flex items-start gap-4">
          <Building2 className="h-4 w-4 text-primary mt-1" />
          <div className="flex-1">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">{u.kind.replace(/_/g, " ")} · {new Date(u.created_at).toLocaleDateString()}</div>
            <div className="font-semibold">{u.title}</div>
            {u.detail && <div className="text-sm text-muted-foreground">{u.detail}</div>}
            <Link to={`/network/${u.org_id}`} className="text-xs text-primary hover:underline mt-1 inline-block">{u.org_name} · {u.org_tier}</Link>
          </div>
          {u.cost_usd && <div className="mono text-xs text-muted-foreground">USD {u.cost_usd.toLocaleString()}</div>}
        </div>
      ))}
    </div>
  );
};

const DocumentsView = ({ asset }) => {
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const assetId = asset?.id || asset?.reference;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {assetId && (
        <a href={`${backendUrl}/api/passport/${assetId}/report.pdf`} target="_blank" rel="noreferrer" className="border-2 border-primary/50 p-5 bg-card hover:border-primary transition-colors" data-testid="passport-report-download">
          <div className="kicker text-primary">Digital Passport Report</div>
          <div className="display text-lg mt-1">Multi-page PDF</div>
          <div className="text-xs text-muted-foreground">Bank · Insurer · Auditor · Legal ready</div>
        </a>
      )}
      {asset?.certificate_number && (
        <a href={`/api/reports/vehicle/${asset.id}.pdf`} target="_blank" rel="noreferrer" className="border border-border p-5 bg-card hover:border-primary/50 transition-colors">
          <div className="kicker">Verification report</div>
          <div className="display text-lg mt-1">Legacy PDF</div>
          <div className="text-xs text-muted-foreground">{asset.certificate_number}</div>
        </a>
      )}
      <a href="#" className="border border-border p-5 bg-card hover:border-primary/50 transition-colors opacity-60 cursor-not-allowed">
        <div className="kicker">Insurance policy</div><div className="display text-lg mt-1">Not on file</div>
      </a>
    </div>
  );
};
